import { fromUnixTime } from 'date-fns/fromUnixTime'
import { addYears } from 'date-fns/addYears'
import { getUnixTime } from 'date-fns/getUnixTime'

import { DATE_FORMATS_INTL, formatDate } from '../constants'
import { type Goal, type GoalsCopy, MetaType, TrackType } from '../index'
import { formatPercentage } from './NumberFormatting'
import { buildDynamicCopy } from './Localization'

export interface GoalStatusData {
  backgroundColor: string
  color: string
  percentage: number
  percentageText: string
  projectedText: string
}

export type GoalType = {
  bgcolor: string
  icon: string
  label: string
  description: string
  subTypes: {
    icon: string
    label: string
    metaType: MetaType
  }[]
  type: TrackType
}

export function getGoalStatusData(copy: GoalsCopy, goal: Goal): GoalStatusData {
  const amount = goal.track_type === TrackType.DEBT_TRACK ? goal.initial_amount : goal.amount

  let percentage = goal.current_amount / amount
  if (percentage > 1) percentage = 1
  // For Debts, the percentage is inversed.
  percentage = goal.track_type === TrackType.DEBT_TRACK ? 1 - percentage : percentage

  // Handle cases where both amounts are 0 producing NaN
  if (isNaN(percentage)) percentage = 1

  let backgroundColor = 'chart.chart1'
  let color = 'common.black'
  if (goal.track_type === TrackType.DEBT_TRACK) {
    color = 'common.white'
    backgroundColor = 'chart.chart5'
  } else if (goal.track_type === TrackType.RETIREMENT_TRACK) {
    backgroundColor = 'chart.chart6'
  }

  const percentageStyle = {
    style: 'percent',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  } as Intl.NumberFormatOptions

  const completionDateUnixTime = goal.is_complete
    ? goal.completed_at
    : goal.projected_to_complete_at
  const completionDate = completionDateUnixTime ? fromUnixTime(completionDateUnixTime) : new Date()

  const percentageText = buildDynamicCopy(
    copy.goal_percentage,
    formatPercentage(percentage, percentageStyle),
  ) as string

  const projectedText = buildDynamicCopy(
    goal.is_complete ? copy.completed_label : copy.goal_projection,
    formatDate(completionDate, DATE_FORMATS_INTL.MONTH_SHORT_YEAR),
  ) as string

  return {
    backgroundColor,
    color,
    percentage,
    percentageText,
    projectedText,
  }
}

export function getGoalTypes(copy: GoalsCopy): GoalType[] {
  return [
    {
      bgcolor: 'chart.chart1',
      icon: 'attach_money',
      label: copy.saving_goal_label,
      description: copy.saving_goal_description,
      subTypes: [
        {
          icon: 'ecg_heart',
          label: copy.saving_goal_emergency_fund,
          metaType: MetaType.EMERGENCY_FUND,
        },
        {
          icon: 'directions_car',
          label: copy.saving_gaol_automobile,
          metaType: MetaType.AUTOMOBILE,
        },
        { icon: 'school', label: copy.saving_goal_college, metaType: MetaType.COLLEGE },
        { icon: 'home', label: copy.saving_goal_home, metaType: MetaType.HOUSE },
        {
          icon: 'mountain_flag',
          label: copy.saving_goal_recreational,
          metaType: MetaType.RECREATIONAL_VEHICLE,
        },
        { icon: 'airplane_ticket', label: copy.saving_goal_vacation, metaType: MetaType.VACATION },
        {
          icon: 'desktop_windows',
          label: copy.saving_goal_electronic,
          metaType: MetaType.ELECTRONIC,
        },
        { icon: 'payments', label: copy.saving_goal_other, metaType: MetaType.OTHER },
      ],
      type: TrackType.SAVINGS_TRACK,
    },
    {
      bgcolor: 'chart.chart5',
      icon: 'note_stack',
      label: copy.debt_goal_label,
      description: copy.debt_goal_description,
      subTypes: [],
      type: TrackType.DEBT_TRACK,
    },
    {
      bgcolor: 'chart.chart6',
      icon: 'person',
      label: copy.retirement_goal_label,
      description: copy.retirement_goal_description,
      subTypes: [],
      type: TrackType.RETIREMENT_TRACK,
    },
  ]
}

export const getZeroStateOngoing = (trackType: TrackType, copy: GoalsCopy) => {
  switch (trackType) {
    case TrackType.DEBT_TRACK:
      return buildDynamicCopy(copy.zero_state_ongoing, 'debt') as string
    case TrackType.SAVINGS_TRACK:
      return buildDynamicCopy(copy.zero_state_ongoing, 'saving') as string
    default:
      return buildDynamicCopy(copy.zero_state_ongoing, 'retirement') as string
  }
}

export const getProjectedRetirementDate = (retirementAge: number, userBirthday: number) => {
  const birthDate = fromUnixTime(userBirthday)
  const retirementDate = addYears(birthDate, retirementAge)
  return getUnixTime(retirementDate)
}
